// @ts-nocheck
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Loader2 } from "lucide-react";
import { WaitlistSection } from "@/design-system/event-editor/WaitlistSection";
import { TrackingSection } from "@/design-system/event-editor/TrackingSection";
import { PayLaterSection } from "@/design-system/event-editor/PayLaterSection";

export type EventOpsPanel = "waitlist" | "tracking" | "pay_later";

const TITLES: Record<EventOpsPanel, string> = {
  waitlist: "Waitlist",
  tracking: "Team Tracking",
  pay_later: "Pay Later",
};

/**
 * Renders the shared Waitlist / Team Tracking / Pay Later panels in a dialog.
 * When `event` / `divisionSettings` aren't supplied (e.g. from the events list
 * card), they are fetched once for the given event id.
 */
export function EventOpsDialog({
  panel,
  eventId,
  onClose,
  event: eventProp,
  divisionSettings: divisionSettingsProp,
}: {
  panel: EventOpsPanel | null;
  eventId: string | undefined;
  onClose: () => void;
  event?: any;
  divisionSettings?: any[];
}) {
  const needsFetch = !eventProp || !divisionSettingsProp;
  const [event, setEvent] = useState<any>(eventProp ?? null);
  const [divisionSettings, setDivisionSettings] = useState<any[]>(divisionSettingsProp ?? []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (eventProp) setEvent(eventProp);
    if (divisionSettingsProp) setDivisionSettings(divisionSettingsProp);
  }, [eventProp, divisionSettingsProp]);

  useEffect(() => {
    if (!panel || !eventId || !needsFetch) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const [evRes, dsRes] = await Promise.all([
        eventProp
          ? Promise.resolve({ data: eventProp })
          : supabase.from("events").select("*").eq("id", eventId).maybeSingle(),
        divisionSettingsProp
          ? Promise.resolve({ data: divisionSettingsProp })
          : supabase.from("event_division_settings").select("*").eq("event_id", eventId),
      ]);
      if (cancelled) return;
      setEvent(evRes?.data ?? null);
      setDivisionSettings(dsRes?.data ?? []);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [panel, eventId, needsFetch, eventProp, divisionSettingsProp]);

  return (
    <Dialog open={!!panel} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{panel ? TITLES[panel] : ""}</DialogTitle>
        </DialogHeader>
        {loading ? (
          <div className="p-10 flex justify-center">
            <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <>
            {panel === "waitlist" && <WaitlistSection event={event} eventId={eventId} />}
            {panel === "tracking" && (
              <TrackingSection
                event={event}
                eventId={eventId}
                divisionSettings={divisionSettings}
              />
            )}
            {panel === "pay_later" && <PayLaterSection eventId={eventId} />}
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

export default EventOpsDialog;
